import { countBy } from 'es-toolkit/array';
import { isEmpty } from 'es-toolkit/compat';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  emailAddressSchema,
  microsoftEmailAddressSchema,
} from '../../../models/schemas/email-address.schema';
import {
  formatEmailAddresses,
  parseEmailAddresses,
} from '../../../shared/functions';
import {
  LeadAssignmentInputType,
  LeadAssignmentLead,
  LeadAssignmentType,
} from './lead-assignment.types';

export class LeadAssignmentUiStore {
  @observable emailAddressInputMap: Map<string, string> = new Map();

  @observable isBeingValidatedMap: Map<string, boolean> = new Map();

  @observable leadAssignmentTypeMap: Map<string, LeadAssignmentType> = new Map();

  @observable leadAssignmentInputTypeMap: Map<string, LeadAssignmentInputType> = new Map();

  @observable requiresMicrosoftEmailAddressMap: Map<string, boolean> = new Map();

  @observable existingEmailAddressesMap: Map<string, string[]> = new Map();

  @observable hasExistingPrimaryLeadMap: Map<string, boolean> = new Map();

  constructor() {
    makeObservable(this);
  }

  @action
  setLeadAssignmentType(key: string, leadAssignmentType: LeadAssignmentType): void {
    this.leadAssignmentTypeMap.set(key, leadAssignmentType);
  }

  @action
  setLeadAssignmentInputType(key: string, leadAssignmentInputType: LeadAssignmentInputType): void {
    this.leadAssignmentInputTypeMap.set(key, leadAssignmentInputType);
  }

  @action
  setRequiresMicrosoftEmailAddress(key: string, requiresMicrosoftEmailAddress: boolean): void {
    this.requiresMicrosoftEmailAddressMap.set(key, requiresMicrosoftEmailAddress);
  }

  @action
  setEmailAddressInput(key: string, emailAddressInput: string): void {
    this.emailAddressInputMap.set(key, emailAddressInput);
  }

  @action
  resetEmailAddressInput(key: string): void {
    this.emailAddressInputMap.set(key, '');
    this.isBeingValidatedMap.set(key, false);
  }

  @action
  setIsBeingValidated(key: string, isBeingValidated: boolean): void {
    this.isBeingValidatedMap.set(key, isBeingValidated);
  }

  @action
  setExistingEmailAddresses(key: string, existingEmailAddresses: string[]): void {
    this.existingEmailAddressesMap.set(key, existingEmailAddresses);
  }

  @action
  setHasExistingPrimaryLead(key: string, hasExistingPrimaryLead: boolean): void {
    this.hasExistingPrimaryLeadMap.set(key, hasExistingPrimaryLead);
  }

  getEmailAddressInput(key: string): string {
    return this.emailAddressInputMap.get(key) ?? '';
  }

  getIsBeingValidated(key: string): boolean {
    return this.isBeingValidatedMap.get(key) ?? false;
  }

  getEmailAddressValidationMessages(key: string): string[] {
    return this.emailAddressValidationMessagesMap.get(key) ?? [];
  }

  getHasExistingPrimaryLeadValidationMessages(key: string): string[] {
    return this.hasExistingPrimaryLeadValidationMessagesMap.get(key) ?? [];
  }

  @action
  getEmailAddressToAdd(key: string, leads: LeadAssignmentLead[]): string | undefined {
    this.setValidationContext(key, leads);

    if (!this.isLeadAssignmentValidMap.get(key)) return;

    const [emailAddress] = this.validEmailAddressesMap.get(key) ?? [];
    if (!emailAddress) return;

    this.resetEmailAddressInput(key);
    return emailAddress;
  }

  @action
  getEmailAddressesToAdd(key: string, leads: LeadAssignmentLead[]): string[] {
    this.setValidationContext(key, leads);

    const validEmailAddresses = this.validEmailAddressesMap.get(key) ?? [];
    const invalidEmailAddresses = this.invalidEmailAddressesMap.get(key) ?? [];

    if (invalidEmailAddresses.length < 1) {
      this.resetEmailAddressInput(key);
    } else {
      this.emailAddressInputMap.set(key, formatEmailAddresses(invalidEmailAddresses));
    }

    return validEmailAddresses;
  }

  @action
  resetStates(key: string): void {
    this.emailAddressInputMap.delete(key);
    this.isBeingValidatedMap.delete(key);
    this.leadAssignmentTypeMap.delete(key);
    this.leadAssignmentInputTypeMap.delete(key);
    this.requiresMicrosoftEmailAddressMap.delete(key);
    this.existingEmailAddressesMap.delete(key);
    this.hasExistingPrimaryLeadMap.delete(key);
  }

  getPrimaryLeadsToDisplay(leads: LeadAssignmentLead[]): LeadAssignmentLead[] {
    return leads.filter((lead) => 'isPrimary' in lead && lead.isPrimary);
  }

  getSecondaryLeadsToDisplay(leads: LeadAssignmentLead[]): LeadAssignmentLead[] {
    return leads.filter((lead) => !('isPrimary' in lead) || !lead.isPrimary);
  }

  @computed
  get emailAddressesMap(): Map<string, string[]> {
    return new Map(
      Array.from(this.emailAddressInputMap, ([key, emailAddressInput]) => {
        const emailAddresses = (this.leadAssignmentInputTypeMap.get(key) ?? LeadAssignmentInputType.Single)
          === LeadAssignmentInputType.Multiple
          ? parseEmailAddresses(emailAddressInput)
          : [emailAddressInput];

        return [key, isEmpty(emailAddresses) ? [''] : emailAddresses];
      }),
    );
  }

  @computed
  get emailAddressValidationMessagesMap(): Map<string, string[]> {
    return new Map(
      Array.from(this.emailAddressesMap, ([key, emailAddresses]) => [
        key,
        emailAddresses.flatMap((emailAddress) =>
          this.getEmailAddressValidationMessagesForValue(key, emailAddress)),
      ]),
    );
  }

  @computed
  get hasExistingPrimaryLeadValidationMessagesMap(): Map<string, string[]> {
    return new Map(
      Array.from(this.hasExistingPrimaryLeadMap, ([key, hasExistingPrimaryLead]) => [
        key,
        hasExistingPrimaryLead
          ? ['Remove the current primary lead before you add another primary lead.']
          : [],
      ]),
    );
  }

  @computed
  get validEmailAddressesMap(): Map<string, string[]> {
    return new Map(
      Array.from(this.emailAddressesMap, ([key, emailAddresses]) => [
        key,
        this.hasExistingPrimaryLeadMap.get(key)
          ? []
          : emailAddresses.filter((emailAddress) =>
            this.getEmailAddressValidationMessagesForValue(key, emailAddress).length === 0),
      ]),
    );
  }

  @computed
  get invalidEmailAddressesMap(): Map<string, string[]> {
    return new Map(
      Array.from(this.emailAddressesMap, ([key, emailAddresses]) => [
        key,
        emailAddresses.filter((emailAddress) =>
          this.getEmailAddressValidationMessagesForValue(key, emailAddress).length > 0),
      ]),
    );
  }

  @computed
  get isLeadAssignmentValidMap(): Map<string, boolean> {
    return new Map(
      Array.from(this.emailAddressesMap.keys(), (key) => [
        key,
        (this.emailAddressValidationMessagesMap.get(key)?.length ?? 0) === 0
          && (this.hasExistingPrimaryLeadValidationMessagesMap.get(key)?.length ?? 0) === 0,
      ]),
    );
  }

  @action
  setValidationContext(key: string, leads: LeadAssignmentLead[]): void {
    this.setIsBeingValidated(key, true);
    this.setExistingEmailAddresses(key, leads.map((lead) => lead.emailAddress));
    this.setHasExistingPrimaryLead(
      key,
      (this.leadAssignmentTypeMap.get(key) ?? LeadAssignmentType.Secondary)
        === LeadAssignmentType.Primary
        && this.getPrimaryLeadsToDisplay(leads).length > 0,
    );
  }

  getEmailAddressValidationMessagesForValue(key: string, emailAddress: string): string[] {
    const schema = this.requiresMicrosoftEmailAddressMap.get(key)
      ? microsoftEmailAddressSchema
      : emailAddressSchema;
    const result = schema.safeParse(emailAddress);
    if (!result.success) return result.error.issues.map((issue) => issue.message);

    if (this.existingEmailAddressesMap.get(key)?.includes(emailAddress)) {
      return [`Remove ${emailAddress} from this entry. This email address is already assigned.`];
    }

    const emailAddressCounts = countBy(
      this.emailAddressesMap.get(key) ?? [],
      (currentEmailAddress) => currentEmailAddress,
    );

    return emailAddressCounts[emailAddress] > 1
      ? [`Remove the duplicate email address ${emailAddress}.`]
      : [];
  }
}
